import { AlertTriangle, X } from 'lucide-react';
import { useEffect } from 'react';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning';
  isLoading?: boolean;
}

export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  isLoading = false
}: ConfirmDialogProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const colors = {
    danger: {
      icon: 'text-[#DC2626]',
      bg: 'bg-[#FEE2E2]',
      button: 'bg-[#DC2626] hover:bg-[#B91C1C]'
    },
    warning: {
      icon: 'text-[#F59E0B]',
      bg: 'bg-[#FEF3C7]',
      button: 'bg-[#F59E0B] hover:bg-[#D97706]'
    }
  };

  const color = colors[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 animate-[fadeIn_0.2s_ease-out]"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-[20px] shadow-2xl max-w-md w-full p-6 animate-[slideUp_0.3s_ease-out]">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F7F8FA] transition-colors"
        >
          <X className="w-5 h-5 text-[#6B7280]" />
        </button>

        {/* Icon */}
        <div className={`w-14 h-14 ${color.bg} rounded-full flex items-center justify-center mb-4`}>
          <AlertTriangle className={`w-7 h-7 ${color.icon}`} />
        </div>

        {/* Content */}
        <h2 className="text-xl font-bold text-[#111827] mb-2">
          {title}
        </h2>
        <p className="text-[#6B7280] mb-6">
          {message}
        </p>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-3 px-6 bg-white border-2 border-[#E5E7EB] text-[#111827] font-semibold rounded-[14px] hover:bg-[#F7F8FA] transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`flex-1 py-3 px-6 ${color.button} text-white font-semibold rounded-[14px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? 'Procesando...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
